import React, { useCallback, useRef, useState } from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import styled from 'styled-components';
import { tokens } from '../../../../../theme/index';
import { SystemIcon } from '../../../../Icon';
import Tooltip, { shouldEnableTooltip } from '../../../../Tooltip/Tooltip';
import { QueryModel, SortModel } from '../../../utils/DataTableQueryProvider/DataTableQuery.types';
import { useDataTableQuickActionsContext } from '../../../utils/DataTableQuickActionsContext';
import { useDataTableQuickReportsContext } from '../../../utils/DataTableQuickReportsContext';
import { OnQuickReportsChanged } from '../../../utils/DataTableTypes';
import { DataTableRowAction, DataTableRowActions } from '../../DataTableRowActions';
import { useDataTableContext } from '../../../utils/DataTableContext';
import { PluginDestination } from '../../../plugins/impl';
import { useIntlUds } from '../../../../../translations/UdsIntlProvider';

interface Props<Q extends QueryModel, S extends SortModel> {
  report: OnQuickReportsChanged<Q, S, unknown, string, string, string, string>;
  isUnSavedState: boolean;
  goToFilterPanel: () => void;
  editReport: (quickReportId: string) => void;
  isDragging: boolean;
}

export const QuickReportActions = <Q extends QueryModel, S extends SortModel>({
  report,
  isUnSavedState,
  goToFilterPanel,
  editReport,
  isDragging
}: Props<Q, S>) => {
  const { intl } = useIntlUds();
  const { isFetching } = useDataTableContext();
  const { setQuickReport, saveCurrentQuickReport, undoChanges, pinQuickReport, unPinQuickReport, selectQuickReport } =
    useDataTableQuickActionsContext();
  const [showTooltip, setTooltipState] = useState(false);
  const typographyRef = useRef<HTMLDivElement>(null);

  const { activeQuickReportId, unSavedQuickReport, onQuickReportClick } = useDataTableQuickReportsContext<Q, S>();
  const isActive = activeQuickReportId === report.quickReportId;
  const isUnSaved = unSavedQuickReport?.quickReportId === report.quickReportId;
  const isPinned = report.pinned;

  const handleQuickReportClick = useCallback(
    (quickReportId: string) => {
      onQuickReportClick?.(quickReportId, 'quick-report-drawer');
      selectQuickReport(quickReportId);
    },
    [onQuickReportClick, selectQuickReport]
  );

  const disableAllActions = isFetching;

  return (
    <StyledQuickReports>
      <PluginDestination name="quickViewsDrawerActionMenu" contextValue={report} />
      <StyledMenuItem
        $isDragging={isDragging}
        className="quick-report-menu-item"
        selected={isActive}
        disabled={disableAllActions || (isUnSavedState && !isActive)}
        onClick={() => !isUnSaved && handleQuickReportClick(report.quickReportId)}
        data-test="data-table-select-quick-report"
        disableGutters
      >
        <SystemIcon style={{ cursor: 'grab' }} size="small" name="drag_indicator" />
        {isUnSaved && <SystemIcon size="small" name="warning" />}
        <StyledName
          ref={typographyRef}
          data-test="quick-report-name"
          onMouseEnter={() => setTooltipState(shouldEnableTooltip(typographyRef?.current))}
        >
          <Tooltip title={report.name} disabled={!showTooltip} dataTest="quick-report-tooltip" placement="top">
            <span>{report.name}</span>
          </Tooltip>
        </StyledName>
        {!!report.schedule && <SystemIcon size="small" name="calendar_today" />}
      </StyledMenuItem>
      <DataTableRowActions
        iconSize="small"
        dataTest={`${report.name}-actions`}
        disabled={disableAllActions || (isUnSavedState && !isActive)}
      >
        <PluginDestination name="quickViewsDrawerActionMenuContent" contextValue={report}>
          <DataTableRowAction
            dataTest="quick-report-edit"
            key="quick-report-edit"
            disabled={disableAllActions}
            iconName="edit"
            onClick={() => (isUnSaved ? goToFilterPanel() : editReport(report.quickReportId))}
          >
            {intl.DataTable.QuickViews.action.edit}
          </DataTableRowAction>
          {isUnSaved && [
            <DataTableRowAction
              dataTest="quick-report-save"
              key="quick-report-save"
              iconName="border_color"
              disabled={disableAllActions}
              onClick={saveCurrentQuickReport}
            >
              {intl.DataTable.QuickViews.action.save}
            </DataTableRowAction>,
            <DataTableRowAction
              dataTest="quick-report-save-as"
              key="quick-report-save-as"
              iconName="save"
              disabled={disableAllActions}
              onClick={() =>
                setQuickReport({
                  name: `CLONE-${report.name}`,
                  quickReportId: report.quickReportId,
                  actionType: 'Save as'
                })
              }
            >
              {intl.DataTable.QuickViews.action.saveAs}
            </DataTableRowAction>,

            <DataTableRowAction
              dataTest="quick-report-undo-changes"
              key="quick-report-undo-changes"
              iconName="restore_from_trash"
              disabled={disableAllActions}
              onClick={undoChanges}
            >
              {intl.DataTable.QuickViews.action.undoChanges}
            </DataTableRowAction>
          ]}
          {!isUnSaved && [
            <DataTableRowAction
              dataTest="quick-report-rename"
              key="quick-report-rename"
              iconName="border_color"
              disabled={disableAllActions}
              onClick={() =>
                setQuickReport({ name: report.name, quickReportId: report.quickReportId, actionType: 'Rename' })
              }
            >
              {intl.DataTable.QuickViews.action.rename}
            </DataTableRowAction>,
            <DataTableRowAction
              disabled={disableAllActions}
              dataTest="quick-report-duplicate"
              key="quick-report-duplicate"
              iconName="file_copy"
              onClick={() =>
                setQuickReport({
                  name: `CLONE-${report.name}`,
                  quickReportId: report.quickReportId,
                  actionType: 'Duplicate'
                })
              }
            >
              {intl.DataTable.QuickViews.action.duplicate}
            </DataTableRowAction>,
            <DataTableRowAction
              dataTest={isPinned ? 'quick-report-unpin' : 'quick-report-pin'}
              key="quick-report-unpin"
              iconName={isPinned ? 'keep' : 'link'}
              disabled={disableAllActions}
              onClick={() => (isPinned ? unPinQuickReport(report.quickReportId) : pinQuickReport(report.quickReportId))}
            >
              {isPinned ? intl.DataTable.QuickViews.action.unpin : intl.DataTable.QuickViews.action.pin}
            </DataTableRowAction>,
            <DataTableRowAction
              dataTest="quick-report-delete"
              key="quick-report-delete"
              iconName="delete"
              style={{ color: tokens.colorAlert }}
              onClick={() =>
                setQuickReport({ name: report.name, quickReportId: report.quickReportId, actionType: 'Delete' })
              }
            >
              {intl.DataTable.QuickViews.action.delete}
            </DataTableRowAction>
          ]}
        </PluginDestination>
      </DataTableRowActions>
    </StyledQuickReports>
  );
};

const StyledName = styled.span`
  text-overflow: ellipsis;
  overflow: hidden;
  padding: 0 ${({ theme }) => theme.spacingSize.s};
`;

const StyledMenuItem = styled(MenuItem)<{ $isDragging: boolean }>`
  &.quick-report-menu-item {
    padding: ${({ theme }) => theme.spacingSize.s};
    flex-basis: 100%;
    gap: ${({ theme }) => theme.spacingSize.s};
    border-radius: ${({ theme }) => theme.radiusDefault};
    box-shadow: ${({ theme, $isDragging }) => ($isDragging ? theme.elevationLevel1 : 'none')};
  }
`;

const StyledQuickReports = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
